import * as XLSX from "xlsx";
import { formatDate, formatDateTime12, formatDateTime24 } from "@/utils/helpers";

function capitalizeWord(s: string): string {
  const t = String(s).trim();
  if (!t) return t;
  return t?.charAt(0)?.toUpperCase() + t?.slice(1)?.toLowerCase();
}
function patientDisplayName(patient: Record<string, unknown> | null | undefined): string {
  if (!patient || typeof patient !== "object") return "—";
  const p = patient as Record<string, unknown>;
  const parts = [p.firstName, p.middleName, p.lastName].filter(
    (x) => x != null && String(x).trim() !== "",
  );

  return parts.length
  ? parts.map((x) => capitalizeWord(String(x)))?.join(" ")
  : "—";
}

function programsDisplay(patient: Record<string, unknown> | null | undefined): string {
  if (!patient || typeof patient !== "object") return "—";
  const raw = (patient as { programs?: unknown }).programs;
  if (!Array.isArray(raw) || raw.length === 0) return "—";
  return raw
    .map((x) => String(x).trim().toUpperCase())
    .filter(Boolean)
    .join(", ");
}

function formatNoteDateTime(note: Record<string, unknown>): string {
  const raw = (note.dateTimeLocal ?? note.dateTime) as string | undefined;
  if (!raw) return "";
  return formatDateTime12(raw);
}

function careTypeDisplay(note: Record<string, unknown>): string {
  const c = note.careType;
  if (Array.isArray(c)) return c.map((x) => String(x)).join(", ");
  if (c == null || c === "") return "";
  return String(c);
}

/**
 * XLSX: patient name, DOB, programs once at top; then one row per nursing note.
 */
export function exportNursingNotesXlsx(
  patient: Record<string, unknown> | null | undefined,
  notes: unknown[],
  fileBase = "nursing-notes",
): void {
  const patientName = patientDisplayName(patient);
  const dob =
    patient && typeof patient === "object" && (patient as { dateOfBirth?: string }).dateOfBirth
      ? formatDate((patient as { dateOfBirth: string }).dateOfBirth)
      : "—";
  const programs = programsDisplay(patient);

  const headerRow = [
    "Program",
    "Care type",
    "Note type",
    "Date & time",
    "Duration (in mins)",
    "Note",
  ];

  const rows: (string | number)[][] = [
    ["Patient name", patientName],
    ["DOB", dob],
    ["Programs", programs],
    [],
    headerRow,
  ];

  for (const item of notes || []) {
    const n = (item && typeof item === "object" ? item : {}) as Record<string, unknown>;
    const duration =
      n.duration != null && n.duration !== "" ? Number(n.duration) : "";
    rows.push([
      String(n.program ?? "").toUpperCase(),
      careTypeDisplay(n),
      String(n.note_type ?? ""),
      formatNoteDateTime(n),
      duration === "" || Number.isNaN(Number(duration)) ? "" : Number(duration),
      String(n.content ?? ""),
    ]);
  }

  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Nursing notes");
  const safe = String(fileBase).replace(/[^\w.-]+/g, "_") || "nursing-notes";
  XLSX.writeFile(wb, `${safe}.xlsx`);
}
